import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FileText, Paperclip, X } from "lucide-react";
import Btn from "../Btn";
import { todoTaskApi } from "../../api/todoTaskApi";

/**
 * Shared pieces of the to-do list — the page, the dashboard card and the
 * login popup all use these, so a task reads the same everywhere.
 */

/** Files per task / step (any kind — web pages, scripts and programs are refused by the server). */
export const MAX_FILES = 5;
export const MAX_IMAGES = MAX_FILES;
const MAX_BYTES = 10 * 1024 * 1024;

/** Every change fires this; the top bar, the dashboard card and the page recount. */
export const TODO_CHANGED = "todo-tasks-changed";
export const notifyTodoChanged = () => window.dispatchEvent(new Event(TODO_CHANGED));

export const unwrap = (res) => res?.data?.data ?? res?.data;
export const errMsg = (e, fallback) => e?.response?.data?.message || fallback;

const apiBase = process.env.REACT_APP_API_BASE_URL || "https://uniformapi.solapurmall.com/api";
const serverRoot = apiBase.endsWith("/api") ? apiBase.slice(0, -4) : apiBase.replace(/\/api\/.*$/, "");
export const fileUrl = (url) => {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : `${serverRoot}${url}`;
};

export const STATUS = {
  // Given, not accepted yet (own tasks from before accepting existed: "To do").
  OPEN: { label: "Not accepted", bg: "var(--info-bg)", fg: "var(--info)" },
  IN_PROGRESS: { label: "In progress", bg: "var(--purple-bg)", fg: "var(--purple)" },
  WAITING: { label: "Waiting", bg: "var(--warn-bg)", fg: "var(--warn)" },
  DONE: { label: "Done · to check", bg: "var(--success-bg)", fg: "var(--success)" },
  // Sent back after the check, or reopened after it was finished.
  REOPENED: { label: "Reopened", bg: "var(--danger-bg)", fg: "var(--danger)" },
  VERIFIED: { label: "Verified", bg: "var(--success-bg)", fg: "var(--success)" },
  CANCELLED: { label: "Cancelled", bg: "var(--bg-hover)", fg: "var(--text-2)" },
  // Outside-the-factory task closed without the work (the party did not deliver).
  NOT_DONE: { label: "Not done", bg: "var(--danger-bg)", fg: "var(--danger)" },
  // Refused by the person it was given to — back with whoever gave it.
  DECLINED: { label: "Declined", bg: "var(--danger-bg)", fg: "var(--danger)" },
};

/** Closed for good — off every list except the history. */
export const isClosedStatus = (s) => s === "VERIFIED" || s === "CANCELLED" || s === "NOT_DONE";

export const PRIORITIES = ["NORMAL", "HIGH", "URGENT"];
export const PRIORITY = {
  NORMAL: { label: "Normal", bg: "var(--bg-hover)", fg: "var(--text-2)" },
  HIGH: { label: "High", bg: "var(--warn-bg)", fg: "var(--warn)" },
  URGENT: { label: "Urgent", bg: "var(--danger-bg)", fg: "var(--danger)" },
};

/** Why a task is on YOUR focus list (summary.top[].actionRole). */
export const ACTION_ROLE = {
  TO_ACCEPT: { label: "To accept", fg: "var(--info)" },
  TO_DO: { label: "Your task", fg: "var(--primary)" },
  DECLINED: { label: "Declined", fg: "var(--danger)" },
  WAITING_ON_YOU: { label: "Waiting on you", fg: "var(--warn)" },
  TO_CHECK: { label: "To check", fg: "var(--success)" },
  YOUR_WAITING: { label: "Held up", fg: "var(--text-2)" },
};

/** The line a history event reads as. */
export function eventLine(m, task) {
  const who = m.mine ? "You" : m.authorName || "Someone";
  const target = m.targetUserName || "";
  switch (m.kind) {
    case "CREATED":
      if (task?.outside) return `${who} created this task — outside the factory`;
      return m.targetUserId && m.targetUserId !== m.authorId
        ? `${who} assigned this to ${target}` : `${who} created this task`;
    case "STARTED": return `${who} started`;
    case "ACCEPTED": return `${who} accepted it`;
    case "DECLINED": return `${who} declined it`;
    case "DATE_CHANGED": return `${who} changed the complete-by date`;
    case "BLOCKED": return `${who} marked it pending on ${target}`;
    case "UNBLOCKED":
      return task && m.authorId !== task.assignedToId ? `${who} finished their part` : `${who} resumed the work`;
    case "DONE": return `${who} marked it done`;
    case "VERIFIED": return `${who} checked and verified it`;
    case "SENT_BACK": return `${who} sent it back`;
    case "EDITED": return `${who} edited the task`;
    case "REASSIGNED":
      return m.body === OFFERED_AGAIN ? `${who} offered it again to ${target}` : `${who} gave it to ${target}`;
    case "CANCELLED": return `${who} cancelled it`;
    case "NOT_DONE": return `${who} closed it as not done`;
    case "REOPENED":
      return m.targetUserId ? `${who} reopened it and gave it to ${target}` : `${who} reopened it`;
    default: return who;
  }
}

/** The server's note on a declined task given back to the same person. */
const OFFERED_AGAIN = "Offered again";

/** The note under a history line — nothing when the line already says it. */
export const eventBody = (m) => (m.kind === "REASSIGNED" && m.body === OFFERED_AGAIN ? null : m.body);

/** Own task: given by yourself to yourself (outside tasks too). */
export const isSelfTask = (t) => Boolean(t) && t.assignedById === t.assignedToId;

/** Status label that names who a WAITING task is held up by. */
export function statusLabel(t) {
  if (!t) return "";
  if (t.status === "WAITING" && t.blockedByName) return `Waiting on ${t.blockedByName}`;
  if (t.status === "OPEN" && isSelfTask(t)) return "To do";
  return STATUS[t.status]?.label || t.status || "";
}

export function StatusChip({ task }) {
  const s = STATUS[task?.status] || STATUS.OPEN;
  return <Chip bg={s.bg} fg={s.fg}>{statusLabel(task)}</Chip>;
}

export function PriorityTag({ priority }) {
  if (!priority || priority === "NORMAL") return null;
  const p = PRIORITY[priority];
  return <Chip bg={p.bg} fg={p.fg} strong>{p.label}</Chip>;
}

/**
 * "Overdue · 23 Sep" (red), "Due today" (amber), "Due 25 Sep". Nothing when
 * there is no due date. (The server's `overdue` also covers a passed
 * complete-by date — that one is shown by PromisedTag.)
 */
export function DueTag({ task }) {
  if (!task?.dueDate) return null;
  const closed = isClosedStatus(task.status);
  const today = localIsoDate(new Date());
  if (task.overdue && !closed && task.dueDate < today) {
    return <Chip bg="var(--danger-bg)" fg="var(--danger)" strong>Overdue · {fmtDay(task.dueDate)}</Chip>;
  }
  if (!closed && task.dueDate === today) return <Chip bg="var(--warn-bg)" fg="var(--warn)" strong>Due today</Chip>;
  return <Chip bg="var(--bg-hover)" fg="var(--text-2)">Due {fmtDay(task.dueDate)}</Chip>;
}

/** Still the assignee's to do — the complete-by date still counts. */
const WITH_ASSIGNEE = ["OPEN", "IN_PROGRESS", "REOPENED", "WAITING"];

/**
 * The assignee's answer to the due date: "Will finish by 30 Sep" — red once
 * it has passed, amber when it is later than the due date. Only while the
 * work is still his (nothing before he accepts, nor once handed in).
 */
export function PromisedTag({ task }) {
  // Only while the work is still his — once handed in or closed the detail keeps it as "Will finish by".
  if (!task?.promisedDate || !WITH_ASSIGNEE.includes(task.status)) return null;
  const today = localIsoDate(new Date());
  if (task.promisedDate < today) {
    return <Chip bg="var(--danger-bg)" fg="var(--danger)" strong title="The date they said they would finish by has passed">
      Late · said {fmtDay(task.promisedDate)}
    </Chip>;
  }
  const afterDue = task.dueDate && task.promisedDate > task.dueDate;
  return (
    <Chip bg={afterDue ? "var(--warn-bg)" : "var(--success-bg)"} fg={afterDue ? "var(--warn)" : "var(--success)"} strong={afterDue}
      title={afterDue ? `Later than the due date (${fmtDay(task.dueDate)})` : "The date they said they will finish by"}>
      Will finish by {fmtDay(task.promisedDate)}
    </Chip>
  );
}

/**
 * For whoever gave the task: has the person accepted it, and seen it?
 * "Not accepted · seen 27 Sep, 10:32" / "Not seen yet". Nothing once
 * accepted, on your own tasks, or outside ones.
 */
export function acceptanceLine(t) {
  if (!t || t.outside || isSelfTask(t) || t.status !== "OPEN") return "";
  return t.assigneeSeenAt ? `Not accepted · seen ${fmtDateTime(t.assigneeSeenAt)}` : "Not accepted · not seen yet";
}

export function Chip({ bg, fg, strong, children, title }) {
  return (
    <span title={title} style={{
      display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999,
      fontSize: 11, fontWeight: strong ? 700 : 600, background: bg, color: fg, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

/** "Outside · Raju Tailor, Akkalkot Road" — a task done by someone with no login. */
export function OutsideTag({ task, full }) {
  if (!task?.outside) return null;
  return (
    <Chip bg="var(--purple-bg)" fg="var(--purple)" strong title={`Outside the factory: ${task.outsideParty || ""}`}>
      Outside{full && task.outsideParty ? ` · ${task.outsideParty}` : ""}
    </Chip>
  );
}

/** "2/3 done" on each copy of a task given to several people. */
export function GroupTag({ task }) {
  if (!task || !(task.groupTotal > 1)) return null;
  const all = task.groupFinished >= task.groupTotal;
  return (
    <Chip bg={all ? "var(--success-bg)" : "var(--bg-hover)"} fg={all ? "var(--success)" : "var(--text-1)"} strong
      title={`Given to ${task.groupTotal} people — ${task.groupFinished} done`}>
      {task.groupFinished}/{task.groupTotal} done
    </Chip>
  );
}

/** Who a task is with, from the viewer's side of it. */
export function peopleLine(t, view) {
  if (t.outside) return `Outside · ${t.outsideParty || ""}`;
  if (t.assignedById === t.assignedToId) return "Your own task";
  if (view === "ASSIGNED_BY_ME") return `For ${t.assignedToName}`;
  if (view === "MINE") return `From ${t.assignedByName}`;
  return `${t.assignedByName} → ${t.assignedToName}`;
}

/** A small dot that marks "new for you". */
export function NewDot({ title = "New for you" }) {
  return <span title={title} style={{
    display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "var(--danger)", flexShrink: 0,
  }} />;
}

/**
 * The CSS for the flashing bits. Injected once; honours "reduce motion".
 * `todo-flash` pulses a badge; `todo-glow` pulses a card border.
 */
export function TodoFlashStyle() {
  return (
    <style>{`
      @keyframes todoFlash { 0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(220,38,38,.55); }
                             50% { transform: scale(1.12); box-shadow: 0 0 0 6px rgba(220,38,38,0); } }
      @keyframes todoGlow { 0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,.35); }
                            50% { box-shadow: 0 0 0 5px rgba(220,38,38,0); } }
      .todo-flash { animation: todoFlash 1.4s ease-in-out infinite; }
      .todo-glow { animation: todoGlow 1.8s ease-in-out infinite; }
      @media (prefers-reduced-motion: reduce) { .todo-flash, .todo-glow { animation: none; } }
    `}</style>
  );
}

// ── files ───────────────────────────────────────────────────────────────

/** "182 KB", "2.4 MB". */
export function fmtSize(bytes) {
  const n = Number(bytes);
  if (!n || n < 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Shown as a picture? Rows from before other files were allowed are all photos. */
export const isImageAttachment = (a) => a?.image !== false;

/**
 * Files attached while a task / step is being written — any kind up to
 * 10 MB (photos are shrunk on the server; web pages, scripts and programs
 * are refused there with the reason). Each pick is uploaded at once (the
 * server keeps it staged for this user) and its id goes with the save.
 * Removing one, or abandoning the draft, deletes the upload on the server
 * too. Same behaviour as support-ticket screenshots.
 */
export function useStagedImages() {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(0);
  const imagesRef = useRef([]);
  const countRef = useRef(0);
  // Bumped when the draft is sent or thrown away: an upload finishing after
  // that belongs to no draft and is deleted, never attached.
  const genRef = useRef(0);

  useEffect(() => { imagesRef.current = images; }, [images]);

  const add = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    const gen = genRef.current;
    for (const file of files) {
      if (genRef.current !== gen) break;
      if (!file || !file.size) {
        window.alert(`"${file?.name || "That file"}" is empty.`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        window.alert(`"${file.name || "That file"}" is ${fmtSize(file.size)} — files can be at most 10 MB.`);
        continue;
      }
      if (countRef.current >= MAX_FILES) {
        window.alert(`At most ${MAX_FILES} files at a time.`);
        break;
      }
      countRef.current += 1;
      setUploading((n) => n + 1);
      try {
        const a = unwrap(await todoTaskApi.uploadImage(file));
        if (genRef.current !== gen) {
          todoTaskApi.discardImage(a.id).catch(() => {});
        } else {
          setImages((list) => [...list, {
            id: a.id, url: a.url, name: a.originalName || file.name,
            image: isImageAttachment(a), size: a.sizeBytes || file.size,
          }]);
        }
      } catch (e) {
        if (genRef.current === gen) {
          countRef.current -= 1;
          window.alert(errMsg(e, `Could not upload "${file.name || "that file"}".`));
        }
      } finally {
        setUploading((n) => n - 1);
      }
    }
  }, []);

  const remove = useCallback((img) => {
    countRef.current = Math.max(0, countRef.current - 1);
    setImages((list) => list.filter((x) => x.id !== img.id));
    todoTaskApi.discardImage(img.id).catch(() => {});
  }, []);

  /** After a successful save — the uploads belong to the task now. */
  const clear = useCallback(() => {
    genRef.current += 1;
    countRef.current = 0;
    imagesRef.current = [];
    setImages([]);
  }, []);

  /** Draft abandoned — delete the uploads on the server as well. */
  const discardAll = useCallback(() => {
    genRef.current += 1;
    imagesRef.current.forEach((img) => todoTaskApi.discardImage(img.id).catch(() => {}));
    imagesRef.current = [];
    countRef.current = 0;
    setImages([]);
  }, []);

  // Leaving the screen with files staged = abandoning them.
  useEffect(() => () => {
    genRef.current += 1;
    imagesRef.current.forEach((img) => todoTaskApi.discardImage(img.id).catch(() => {}));
  }, []);

  return { images, uploading, add, remove, clear, discardAll };
}

/** The same hook under the name it has now. */
export const useStagedFiles = useStagedImages;

/** A file that is not a picture: its name and size; opens in a new tab (PDF) or downloads. */
export function FileChip({ name, size, url, onRemove }) {
  const label = name || "File";
  const body = (
    <>
      <FileText size={14} style={{ flexShrink: 0 }} />
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }}>{label}</span>
      {size ? <span style={{ color: "var(--text-2)", fontWeight: 500 }}>{fmtSize(size)}</span> : null}
    </>
  );
  const style = {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 9px", borderRadius: 8, fontSize: 12,
    fontWeight: 600, border: "1px solid var(--border)", background: "var(--bg-card)", color: "var(--text-0)",
    textDecoration: "none", maxWidth: 280,
  };
  // The server downloads files that are not photos / PDFs; `name` is what they are saved as.
  const href = url ? `${fileUrl(url)}${url.includes("?") ? "&" : "?"}name=${encodeURIComponent(label)}` : null;
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      {href
        ? <a href={href} target="_blank" rel="noreferrer" title={`Open ${label}`} style={style}>{body}</a>
        : <span title={label} style={style}>{body}</span>}
      {onRemove && (
        <button type="button" title="Remove" onClick={onRemove} style={removeDot}><X size={11} /></button>
      )}
    </span>
  );
}

const removeDot = {
  position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", border: "none",
  background: "var(--danger)", color: "#fff", cursor: "pointer", display: "flex",
  alignItems: "center", justifyContent: "center", padding: 0,
};

export function ImageTray({ staged, onOpen, disabled }) {
  const inputRef = useRef(null);
  const { images, uploading } = staged;
  const full = images.length + uploading >= MAX_FILES;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      {images.map((img) => (img.image !== false ? (
        <div key={img.id} style={{ position: "relative", width: 52, height: 52 }}>
          <img src={fileUrl(img.url)} alt="Attached" title={img.name || "Photo"}
            onClick={() => onOpen(fileUrl(img.url))}
            style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border)", cursor: "zoom-in" }} />
          <button type="button" title="Remove" onClick={() => staged.remove(img)} style={removeDot}><X size={11} /></button>
        </div>
      ) : (
        <FileChip key={img.id} name={img.name} size={img.size} url={img.url} onRemove={() => staged.remove(img)} />
      )))}
      {uploading > 0 && <span style={{ fontSize: 12, color: "var(--text-2)" }}>Uploading {uploading}…</span>}
      <input ref={inputRef} type="file" multiple hidden onChange={(e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        staged.add(files);
      }} />
      <Btn variant="ghost" size="xs" icon={<Paperclip size={13} />} disabled={disabled || full}
        onClick={() => inputRef.current?.click()}>
        {images.length ? "Add file" : "Attach file"}
      </Btn>
      <span style={{ fontSize: 11.5, color: "var(--text-2)" }}>photos or any file · up to {MAX_FILES}, 10 MB each</span>
    </div>
  );
}

/** Files already on a step — photos open full screen, other files in a new tab. */
export function PhotoRow({ attachments, onOpen }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6, alignItems: "center" }}>
      {attachments.map((a) => (isImageAttachment(a) ? (
        <img key={a.id} src={fileUrl(a.url)} alt={a.originalName || "Photo"} title={a.originalName || "Photo"}
          onClick={() => onOpen(fileUrl(a.url))}
          style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border)", cursor: "zoom-in" }} />
      ) : (
        <FileChip key={a.id} name={a.originalName} size={a.sizeBytes} url={a.url} />
      )))}
    </div>
  );
}

/** Everyone who has opened the task — first and latest time. */
export function SeenList({ seenBy }) {
  if (!Array.isArray(seenBy) || seenBy.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {seenBy.map((s) => {
        const again = s.lastSeenAt && s.firstSeenAt && s.lastSeenAt !== s.firstSeenAt;
        return (
          <div key={s.userId} style={{ display: "flex", gap: 8, fontSize: 12.5, flexWrap: "wrap", alignItems: "baseline" }}>
            <b style={{ color: "var(--text-0)", minWidth: 120 }}>{s.me ? `${s.name} (you)` : s.name}</b>
            <span style={{ color: "var(--text-1)" }}>
              {s.firstSeenAt ? `first ${fmtDateTime(s.firstSeenAt)}` : "seen (time not recorded)"}
              {again ? ` · last ${fmtDateTime(s.lastSeenAt)}` : ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function Lightbox({ url, onClose }) {
  useEffect(() => {
    if (!url) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [url, onClose]);
  if (!url) return null;
  // Portalled to <body>, above the modals (they portal too).
  return createPortal(
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,0.82)", display: "flex",
      alignItems: "center", justifyContent: "center", padding: 28, cursor: "zoom-out",
    }}>
      <img src={url} alt="Photo" style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 6, boxShadow: "0 10px 40px rgba(0,0,0,0.5)" }} />
      <a href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
        style={{ position: "absolute", top: 14, right: 20, color: "#fff", fontSize: 13, textDecoration: "underline" }}>
        Open full size
      </a>
    </div>,
    document.body
  );
}

// ── form bits ───────────────────────────────────────────────────────────

export function Field({ label, hint, children, last }) {
  return (
    <div style={{ marginBottom: last ? 0 : 14 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-1)", marginBottom: 5 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: "var(--text-2)", marginTop: 5 }}>{hint}</div>}
    </div>
  );
}

export const inputStyle = {
  width: "100%", padding: "8px 11px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 14,
  background: "var(--bg-input)", color: "var(--text-0)", outline: "none", fontFamily: "inherit", boxSizing: "border-box",
};

// ── dates ───────────────────────────────────────────────────────────────

/** yyyy-mm-dd in the browser's own day (not UTC — 1 a.m. in India is still "today"). */
export function localIsoDate(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** "2026-09-25" → "25 Sep". */
export function fmtDay(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso);
  return new Date(y, m - 1, d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

export function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const minutes = Math.floor(Math.max(0, Date.now() - t) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(t).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
